import type { ReactNode } from 'react';
import * as DM from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import { cx } from '@/lib/cx';

export const Menu = DM.Root;
export const MenuTrigger = DM.Trigger;
export const MenuGroup = DM.Group;

export function MenuContent({ children, align = 'end', className, sideOffset = 6, minWidth = 200 }: { children: ReactNode; align?: 'start' | 'center' | 'end'; className?: string; sideOffset?: number; minWidth?: number }) {
  return (
    <DM.Portal>
      <DM.Content align={align} sideOffset={sideOffset} collisionPadding={8} className={cx('menu', className)} style={{ minWidth }}>
        {children}
      </DM.Content>
    </DM.Portal>
  );
}

export interface MenuItemProps {
  children: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  shortcut?: ReactNode;
  description?: ReactNode;
}

export function MenuItem({ children, icon, onSelect, tone = 'default', disabled, shortcut, description }: MenuItemProps) {
  return (
    <DM.Item className={cx('menu-item', tone === 'danger' && 'is-danger')} onSelect={onSelect} disabled={disabled}>
      {icon && <span className="menu-icon" aria-hidden>{icon}</span>}
      <span className="menu-text">
        <span>{children}</span>
        {description && <span className="menu-desc">{description}</span>}
      </span>
      {shortcut && <span className="menu-shortcut">{shortcut}</span>}
    </DM.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DM.Label className="menu-label">{children}</DM.Label>;
}

export function MenuSeparator() {
  return <DM.Separator className="menu-sep" />;
}

export function MenuRadioGroup({ value, onValueChange, children }: { value: string; onValueChange: (v: string) => void; children: ReactNode }) {
  return (
    <DM.RadioGroup value={value} onValueChange={onValueChange}>
      {children}
    </DM.RadioGroup>
  );
}

export function MenuRadioItem({ value, children, icon }: { value: string; children: ReactNode; icon?: ReactNode }) {
  return (
    <DM.RadioItem value={value} className="menu-item">
      {icon && <span className="menu-icon" aria-hidden>{icon}</span>}
      <span className="menu-text">{children}</span>
      <DM.ItemIndicator className="menu-check">
        <Check size={14} aria-hidden />
      </DM.ItemIndicator>
    </DM.RadioItem>
  );
}
